import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import './Navbar.css';
import config from '../config';
import { useGenies } from './GenieContext';
import api from '../api';

// Cookie-based Authentication (tt-auth)
function Navbar({ isLanding }) {
  const genieContext = useGenies();
  const location = useLocation();
  const isAdminPage = location.pathname === '/admin/dashboard' || location.pathname === '/admin' || location.pathname.startsWith('/admin/');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isClientDropdownOpen, setIsClientDropdownOpen] = useState(false);
  const [userClients, setUserClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [loadingClients, setLoadingClients] = useState(false);
  const navigate = useNavigate();

  // Handle case where Navbar is rendered outside GenieProvider (e.g., landing page)
  const isAuthenticated = genieContext?.isAuthenticated || false;
  const currentUser = genieContext?.currentUser || null;
  // Disable client switching while a chat response is being processed
  const isProcessing = genieContext?.isProcessing || false;
  
  // Extract first name from display name or email, avoiding showing full email
  const getDisplayName = () => {
    if (!currentUser) return 'User';
    
    // If displayName exists and is not an email, use it
    if (currentUser.displayName && !currentUser.displayName.includes('@')) {
      return currentUser.displayName;
    }
    
    // If we have mail, extract first part before @
    if (currentUser.mail) {
      const emailPart = currentUser.mail.split('@')[0];
      // Capitalize first letter and replace dots/underscores with spaces
      return emailPart
        .split(/[._]/)
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
    }
    
    return 'User';
  };
  
  const userName = getDisplayName();
  const userRole = (currentUser?.role || '').toLowerCase().replace(' ', '_');
  const isAdmin = userRole === 'admin';
  const isSuperAdmin = userRole === 'super_admin';
  const isTOSAdmin = isSuperAdmin;
  
  // Fetch user's assigned clients
  useEffect(() => {
    const fetchUserClients = async () => {
      if (!isAuthenticated) {
        console.log('[NAVBAR] Skipping client fetch - not authenticated');
        return;
      }
      
      const userEmail = currentUser?.email || currentUser?.mail;
      if (!userEmail) {
        console.log('[NAVBAR] Skipping client fetch - no email available yet', { currentUser });
        return;
      }
      
      console.log(`[NAVBAR] Fetching clients for user: ${userEmail}`);
      setLoadingClients(true);
      
      try {
        const response = await api.get(`/api/user-clients?email=${encodeURIComponent(userEmail)}`);
        const clients = response.data.clients || [];
        console.log(`[NAVBAR] Received ${clients.length} clients:`, clients);
        setUserClients(clients);
        
        // Set initial selected client only if not already set
        if (clients.length > 0 && !selectedClient) {
          const storedClient = localStorage.getItem('selectedClient');
          if (storedClient && clients.includes(storedClient)) {
            setSelectedClient(storedClient);
            console.log(`[NAVBAR] Selected stored client: ${storedClient}`);
            // Do NOT dispatch clientChanged here on page load — GenieContext handles
            // the initial fetch via fetchData (all genies after auth).
            // clientChanged is only dispatched when the user actively changes client.
          } else {
            // Stored client is invalid or doesn't exist, clear it and select first available
            if (storedClient) {
              console.log(`[NAVBAR] Clearing invalid stored client: ${storedClient}`);
              localStorage.removeItem('selectedClient');
            }
            setSelectedClient(clients[0]);
            localStorage.setItem('selectedClient', clients[0]);
            console.log(`[NAVBAR] Selected first client: ${clients[0]}`);
            // Notify other components of client change
            window.dispatchEvent(new Event('clientChanged'));
          }
        } else if (clients.length === 0) {
          // User has no clients - clear any stored client
          const storedClient = localStorage.getItem('selectedClient');
          if (storedClient) {
            console.log(`[NAVBAR] User has no clients, clearing stored client: ${storedClient}`);
            localStorage.removeItem('selectedClient');
            setSelectedClient(null);
            // Notify other components that client was cleared
            window.dispatchEvent(new Event('clientChanged'));
          }
        }
      } catch (error) {
        console.error('[NAVBAR] Error fetching user clients:', error);
      } finally {
        setLoadingClients(false);
      }
    };

    // Only fetch if we have both authentication and email
    const userEmail = currentUser?.email || currentUser?.mail;
    console.log('[NAVBAR] useEffect check:', {
      isAuthenticated,
      hasEmail: !!userEmail,
      email: userEmail,
      mail: currentUser?.mail,
      willFetch: isAuthenticated && !!userEmail
    });
    
    if (isAuthenticated && userEmail) {
      fetchUserClients();
    } else {
      console.log('[NAVBAR] Skipping fetch - conditions not met');
    }
    
    // Listen for client assignment changes
    const handleClientUpdate = () => {
      console.log('[NAVBAR] Client update event received, re-fetching clients');
      const userEmail = currentUser?.email || currentUser?.mail;
      if (isAuthenticated && userEmail) {
        fetchUserClients();
      }
    };
    
    window.addEventListener('clientsUpdated', handleClientUpdate);
    
    return () => {
      window.removeEventListener('clientsUpdated', handleClientUpdate);
    };
  // Remove selectedClient from dependencies to prevent infinite loop
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, currentUser?.email, currentUser?.mail]);
  
  // Log current state for debugging
  useEffect(() => {
    console.log('[NAVBAR] Render state:', {
      isAuthenticated,
      hasCurrentUser: !!currentUser,
      userName,
      userClients,
      selectedClient,
      contextAuthenticated: genieContext?.isAuthenticated
    });
  }, [isAuthenticated, currentUser, userName, userClients, selectedClient, genieContext]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside both dropdowns
      const userDropdown = document.querySelector('.navbar-user-dropdown');
      const clientDropdown = document.querySelector('.navbar-client-selector');
      
      if (userDropdown && !userDropdown.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      
      if (clientDropdown && !clientDropdown.contains(event.target)) {
        setIsClientDropdownOpen(false);
      }
    };

    // Add event listener when either dropdown is open
    if (isDropdownOpen || isClientDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen, isClientDropdownOpen]);

  // Handle client selection
  const handleClientSelect = (client) => {
    setSelectedClient(client);
    localStorage.setItem('selectedClient', client);
    setIsClientDropdownOpen(false);
    window.dispatchEvent(new Event('clientChanged'));
  };

  const handleLogout = () => {
    setIsDropdownOpen(false);
    console.log('[NAVBAR] 🚪 Redirecting to tt-auth logout...');
    // tt-auth logout endpoint clears the __ttauth cookie and redirects back
    window.location.href = `${config.apiUrl}/ttauth/v1/cookie/logout`;
  };

  return (
    <nav className={`navbar ${isLanding ? 'navbar-landing' : ''} ${isAdminPage ? 'navbar-admin' : ''}`}>
      <div className="navbar-container">
        <div className="navbar-brand">
          <i className="fa-solid fa-arrow-left navbar-back-arrow"></i>
          <Link to={isAuthenticated ? "/home" : "/"} className="navbar-brand-link">
            <img src="/TTLogo.png" alt="Transit Technologies" className="navbar-logo-icon" />
            <span className="ai-suite-badge">AI SUITE</span>
          </Link>
        </div>
        <div className="navbar-actions">
          {isAuthenticated ? (
            <>
              {/* Client Selector/Display */}
              {loadingClients ? (
                <div className="navbar-client-selector">
                  <div className="navbar-client-tile navbar-client-loading">
                    <div className="navbar-skeleton-loader"></div>
                  </div>
                </div>
              ) : userClients.length > 0 && (
                <div className="navbar-client-selector">
                  {userClients.length === 1 ? (
                    <div className="navbar-client-tile">
                      <span className="navbar-client-name">{userClients[0]}</span>
                    </div>
                  ) : (
                    <>
                      <button
                        className="navbar-client-dropdown-trigger"
                        onClick={() => !isProcessing && setIsClientDropdownOpen(!isClientDropdownOpen)}
                        disabled={isProcessing}
                        title={isProcessing ? 'Please wait for the current response to complete' : undefined}
                      >
                        <span className="navbar-client-name">{selectedClient || 'Select Client'}</span>
                        <svg
                          className={`navbar-dropdown-icon ${isClientDropdownOpen ? 'open' : ''}`}
                          width="12"
                          height="8"
                          viewBox="0 0 12 8"
                          fill="none"
                        >
                          <path d="M1 1L6 6L11 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      </button>
                      {isClientDropdownOpen && (
                        <div className="navbar-dropdown-menu">
                          <div className="navbar-dropdown-menu-inner">
                            {userClients.map((client) => (
                              <button
                                key={client}
                                className={`navbar-dropdown-item ${selectedClient === client ? 'active' : ''}`}
                                onClick={() => handleClientSelect(client)}
                              >
                                {client}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* User Dropdown */}
              <div className="navbar-user-dropdown">
                <button
                  className="navbar-dropdown-trigger"
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                >
                  {/* User account circle icon */}
                  <svg
                    className="navbar-user-icon"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
                  </svg>
                  <span className="navbar-username">{userName}</span>
                  <svg
                    className={`navbar-dropdown-icon ${isDropdownOpen ? 'open' : ''}`}
                    width="12"
                    height="8"
                    viewBox="0 0 12 8"
                    fill="none"
                  >
                    <path d="M1 1L6 6L11 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>
                {isDropdownOpen && (
                  <div className="navbar-dropdown-menu">
                    <div className="navbar-dropdown-menu-inner">
                      {(isTOSAdmin || isAdmin) && (
                        <button className="navbar-dropdown-item" onClick={() => { setIsDropdownOpen(false); navigate('/admin/tos'); }}>
                          Settings
                        </button>
                      )}
                      <button className="navbar-dropdown-item logout" onClick={handleLogout}>
                        Log Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <a
              href={`${config.apiUrl}/ttauth/v1/cookie/login-redirect?returnUrl=${encodeURIComponent(
                isLanding
                  ? `${window.location.origin}/`
                  : window.location.href
              )}`}
              className="btn-login"
            >
              Log In
            </a>
          )}
        </div>
      </div>
    </nav>
  );
}

export default Navbar;
